import React from 'react';
import { Button } from './ui/Button';
import { track } from '../lib/analytics';
const CALENDAR_URL = 'https://meetings-eu1.hubspot.com/cavalla';
const EMBED_SRC = `${CALENDAR_URL}?embed=true`;
const SCRIPT_SRC = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
const CONSENT_KEY = 'cavalla.consent';
interface MeetingSchedulerProps {
  /** Passed to discovery_call_booked so the booking is attributed to a service line. */
  line?: string;
  className?: string;
}

/**
 * The HubSpot Meetings embed for discovery calls, on the EU1 portal.
 *
 * The embed sets third party cookies, so it is loaded only after the visitor has
 * accepted all cookies. Until then we show the reason and a direct link, which
 * keeps the page GDPR compliant without hiding the option.
 */
export function MeetingScheduler({
  line,
  className = ''
}: MeetingSchedulerProps) {
  const [consented, setConsented] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const readConsent = React.useCallback(() => {
    try {
      return window.localStorage.getItem(CONSENT_KEY) === 'all';
    } catch {
      return false;
    }
  }, []);
  React.useEffect(() => {
    setConsented(readConsent());
    // The consent banner writes to localStorage, so re-check when it closes.
    const onStorage = () => setConsented(readConsent());
    window.addEventListener('storage', onStorage);
    const poll = window.setInterval(() => {
      if (readConsent()) {
        setConsented(true);
        window.clearInterval(poll);
      }
    }, 1500);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.clearInterval(poll);
    };
  }, [readConsent]);
  React.useEffect(() => {
    if (!consented) return undefined;
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      setLoaded(true);
      return undefined;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => setLoaded(true);
    script.onerror = () => setFailed(true);
    document.body.appendChild(script);
    return undefined;
  }, [consented]);

  /** Fires once, when the visitor first interacts with the embedded scheduler. */
  React.useEffect(() => {
    if (!loaded) return undefined;
    let fired = false;
    const onMessage = (event: MessageEvent) => {
      if (fired) return;
      if (typeof event.origin === 'string' && !event.origin.includes('hubspot.com')) return;
      const data = event.data as {
        meetingBookSucceeded?: boolean;
      } | undefined;
      if (data && data.meetingBookSucceeded) {
        fired = true;
        track('discovery_call_booked', {
          line
        });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [loaded, line]);
  if (!consented) {
    return <div className={`rounded-sm border border-line bg-white p-6 ${className}`} data-id="element-217" data-mp="2txc18hvkyi1j">
        <p className="text-[15px] font-medium text-ink" data-id="element-218" data-mp="2tv8zj0l2f0gz">The scheduler needs your cookie consent</p>
        <p className="mt-2 text-[15px] text-body" data-id="element-219" data-mp="2ypn2bo2249vo">
          Our booking calendar is provided by HubSpot and sets its own cookies. Accept all cookies to load it
          here, or open it in a new tab instead.
        </p>
        <Button href={CALENDAR_URL} target="_blank" variant="secondary" size="sm" className="mt-5" onClick={() => track('discovery_call_booked', {
        line
      })} data-id="element-220" data-mp="3dlcgj0103ipu">
          Open the calendar in a new tab
        </Button>
      </div>;
  }
  if (failed) {
    return <div className={`rounded-sm border border-line bg-white p-6 ${className}`} data-id="element-221" data-mp="38kop4d8v0zv3">
        <p className="text-[15px] font-medium text-ink" data-id="element-222" data-mp="1s7gmchj9fg08">The calendar did not load</p>
        <p className="mt-2 text-[15px] text-body" data-id="element-223" data-mp="1x7chrsdw5z46">
          Please open it directly, or email us and we will send you a time.
        </p>
        <Button href={CALENDAR_URL} target="_blank" variant="secondary" size="sm" className="mt-5" data-id="element-224" data-mp="2ci12cl9y50pu">
          Open the calendar in a new tab
        </Button>
      </div>;
  }
  return <div className={className} data-id="element-225" data-mp="23oxs0lull3zf">
      {!loaded && <p role="status" className="py-8 text-center text-[15px] text-muted" data-id="element-226" data-mp="1yp1kwdqlswfn">
          Loading available times
        </p>}
      <div className="meetings-iframe-container min-h-[680px]" data-src={EMBED_SRC} aria-label="Book a discovery call" data-id="element-227" data-mp="2veao7ur3avke" />
    </div>;
}